import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Polyline, useMap } from 'react-leaflet';
import { useLeafletContext } from '@react-leaflet/core';
import L from 'leaflet';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import 'leaflet/dist/leaflet.css';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

// Fix Leaflet default marker icon issue
delete L?.Icon?.Default?.prototype?._getIconUrl;
L?.Icon?.Default?.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const DrawingControls = ({ onGeometryChange, initialGeometry, editMode }) => {
  const context = useLeafletContext();
  const map = context?.map;
  const drawnLayerRef = useRef(null);

  useEffect(() => {
    if (!map) return;

    // Configure Geoman drawing tools
    map?.pm?.addControls({
      position: 'topleft',
      drawPolyline: editMode,
      drawMarker: false,
      drawCircleMarker: false,
      drawPolygon: false,
      drawCircle: false,
      drawRectangle: false,
      editMode: editMode,
      dragMode: false,
      cutPolygon: false,
      removalMode: editMode,
    });

    // Load initial geometry if exists
    if (initialGeometry?.coordinates && !drawnLayerRef?.current) {
      const latLngs = initialGeometry?.coordinates?.map(coord => [coord?.[1], coord?.[0]]);
      const polyline = L?.polyline(latLngs, {
        color: '#3b82f6',
        weight: 4,
        opacity: 0.8
      })?.addTo(map);
      
      drawnLayerRef.current = polyline;
      
      if (latLngs?.length > 0) {
        map?.fitBounds(polyline?.getBounds(), { padding: [50, 50] });
      }
    }

    // Handle polyline creation
    const handleCreate = (e) => {
      // Remove previous drawn layer
      if (drawnLayerRef?.current) {
        map?.removeLayer(drawnLayerRef?.current);
      }
      
      drawnLayerRef.current = e?.layer;
      const latLngs = e?.layer?.getLatLngs();
      const coordinates = latLngs?.map(latLng => [latLng?.lng, latLng?.lat]);
      
      onGeometryChange({
        type: 'LineString',
        coordinates: coordinates
      });
    };

    // Handle polyline edit
    const handleEdit = (e) => {
      const layers = e?.layers;
      layers?.eachLayer((layer) => {
        const latLngs = layer?.getLatLngs();
        const coordinates = latLngs?.map(latLng => [latLng?.lng, latLng?.lat]);
        
        onGeometryChange({
          type: 'LineString',
          coordinates: coordinates
        });
      });
    };

    // Handle polyline removal
    const handleRemove = (e) => {
      if (drawnLayerRef?.current === e?.layer) {
        drawnLayerRef.current = null;
        onGeometryChange(null);
      }
    };

    map?.on('pm:create', handleCreate);
    map?.on('pm:edit', handleEdit);
    map?.on('pm:remove', handleRemove);

    return () => {
      map?.off('pm:create', handleCreate);
      map?.off('pm:edit', handleEdit);
      map?.off('pm:remove', handleRemove);
      map?.pm?.removeControls();
      
      if (drawnLayerRef?.current) {
        map?.removeLayer(drawnLayerRef?.current);
        drawnLayerRef.current = null;
      }
    };
  }, [map, editMode, initialGeometry, onGeometryChange]);

  return null;
};

const MapUpdater = ({ center, geometry }) => {
  const map = useMap();

  useEffect(() => {
    if (geometry?.coordinates && geometry?.coordinates?.length > 0) {
      const latLngs = geometry?.coordinates?.map(coord => [coord?.[1], coord?.[0]]);
      const bounds = L?.latLngBounds(latLngs);
      map?.fitBounds(bounds, { padding: [50, 50] });
    } else if (center) {
      map?.setView(center, 13);
    }
  }, [map, center, geometry]);

  return null;
};

const InteractiveMapEditor = ({ 
  selectedRoute, 
  geometry, 
  onGeometryChange,
  onSave,
  isSaving 
}) => {
  const [editMode, setEditMode] = useState(false);
  const [currentGeometry, setCurrentGeometry] = useState(null);
  const [mapCenter, setMapCenter] = useState([37.8746, 32.4932]);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (geometry) {
      setCurrentGeometry(geometry);
      if (geometry?.coordinates && geometry?.coordinates?.length > 0) {
        const midIndex = Math.floor(geometry?.coordinates?.length / 2);
        setMapCenter([geometry?.coordinates?.[midIndex]?.[1], geometry?.coordinates?.[midIndex]?.[0]]);
      }
    } else {
      setCurrentGeometry(null);
    }
    setEditMode(false);
    setHasChanges(false);
  }, [geometry, selectedRoute]);

  const handleGeometryChange = (newGeometry) => {
    setCurrentGeometry(newGeometry);
    setHasChanges(true);
    onGeometryChange(newGeometry);
  };

  const handleSave = () => {
    if (!currentGeometry?.coordinates || currentGeometry?.coordinates?.length < 2) {
      alert('Rota en az 2 nokta içermelidir.');
      return;
    }
    onSave();
    setHasChanges(false);
    setEditMode(false);
  };

  const handleCancel = () => {
    setCurrentGeometry(geometry);
    setEditMode(false);
    setHasChanges(false);
    onGeometryChange(geometry);
  };

  const calculateDistance = () => {
    if (!currentGeometry?.coordinates || currentGeometry?.coordinates?.length < 2) return 0;
    
    let totalDistance = 0;
    for (let i = 0; i < currentGeometry?.coordinates?.length - 1; i++) {
      const [lng1, lat1] = currentGeometry?.coordinates?.[i];
      const [lng2, lat2] = currentGeometry?.coordinates?.[i + 1];
      totalDistance += getDistanceBetweenPoints(lat1, lng1, lat2, lng2);
    }
    return totalDistance;
  };

  const getDistanceBetweenPoints = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3;
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  };

  if (!selectedRoute) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-muted/30 rounded-lg p-8 text-center">
        <div className="flex items-center justify-center w-20 h-20 bg-muted rounded-full mb-4">
          <Icon name="Map" size={40} color="var(--color-muted-foreground)" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">Rota Seçin</h3>
        <p className="text-sm text-muted-foreground max-w-md">
          Geometri düzenlemek için sol panelden bir rota seçin
        </p>
      </div>
    );
  }

  const distance = calculateDistance();

  return (
    <div className="flex flex-col h-full bg-card rounded-lg overflow-hidden">
      <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg md:text-xl font-bold text-foreground font-heading line-clamp-1">
            {selectedRoute?.route_name}
          </h3>
          <p className="text-xs md:text-sm text-muted-foreground line-clamp-1">
            {selectedRoute?.start_point} → {selectedRoute?.end_point}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {!editMode ? (
            <Button
              variant="default"
              size="sm"
              iconName="Edit"
              iconPosition="left"
              onClick={() => setEditMode(true)}
            >
              Düzenle
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
              >
                İptal
              </Button>
              <Button
                variant="default"
                size="sm"
                iconName="Save"
                iconPosition="left"
                onClick={handleSave}
                loading={isSaving}
                disabled={!currentGeometry?.coordinates || currentGeometry?.coordinates?.length < 2}
              >
                Kaydet
              </Button>
            </>
          )}
        </div>
      </div>
      {distance > 0 && (
        <div className="px-4 md:px-6 py-3 bg-muted/30 border-b border-border">
          <div className="flex items-center gap-2">
            <Icon name="Ruler" size={16} color="var(--color-primary)" />
            <span className="text-sm font-medium text-foreground">
              Toplam Mesafe: {(distance / 1000)?.toFixed(2)} km
            </span>
          </div>
        </div>
      )}
      <div className="flex-1 relative">
        <MapContainer
          center={mapCenter}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
          className="z-0"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {currentGeometry?.coordinates && currentGeometry?.coordinates?.length > 0 && (
            <Polyline
              positions={currentGeometry?.coordinates?.map(coord => [coord?.[1], coord?.[0]])}
              color="#3b82f6"
              weight={4}
              opacity={0.8}
            />
          )}

          <DrawingControls
            onGeometryChange={handleGeometryChange}
            initialGeometry={currentGeometry}
            editMode={editMode}
          />
          
          <MapUpdater center={mapCenter} geometry={currentGeometry} />
        </MapContainer>

        {editMode && (
          <div className="absolute top-4 right-4 bg-card rounded-lg shadow-lg p-4 space-y-3 max-w-xs z-1000">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">Düzenleme Modu</span>
              <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-full">
                <Icon name="Edit" size={16} color="var(--color-primary)" />
              </div>
            </div>
            <div className="text-xs text-muted-foreground space-y-1">
              <p>• Yeni çizgi çizmek için sol üstteki çizgi aracını kullanın</p>
              <p>• Mevcut çizgiyi düzenlemek için düzenle aracını kullanın</p>
              <p>• Çizgiyi silmek için silme aracını kullanın</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InteractiveMapEditor;